import { ArrowSquareOut, Warning } from "@phosphor-icons/react";
import { Button, Dialog, Flex, Text, Tooltip } from "@radix-ui/themes";
import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { type CitationTarget } from "./AssetViewerPane";

import { Preview } from "../../../components/Preview";
import { knowledgeDatabaseRoute } from "../../../routes/settings/routes/knowledge-database/route";
import { formatTimeStamp } from "../../../routes/tickets/components/VideoPlayback";
import { type KnowledgeDataRecordProperty } from "../../../utils/parsers/knowledge-db";
import { useGetKnowledgeDataRecord } from "../../../utils/queries/knowledge-db";
import { type TicketMessageCitation } from "../utils/commentSourceUtils";
import {
  citationLabel,
  isFileCitation,
  isKnowledgeDbCitation,
  isNavigableFileCitation,
  isTextCitation,
} from "../utils/commentSourceUtils";

function fileCitationToTarget(citation: {
  url?: string;
  pageNumber?: number;
  originalURL?: string;
  rel_x?: number;
  rel_y?: number;
  videoStart?: number;
  cellAnchor?: CitationTarget["cellAnchor"];
}): CitationTarget {
  const page = citation.pageNumber ?? 1;
  const pos =
    citation.rel_x != null && citation.rel_y != null
      ? { x: citation.rel_x, y: citation.rel_y }
      : undefined;
  return {
    pageNumber: page,
    position: pos,
    sourceUrl: citation.url,
    originalURL: citation.originalURL,
    videoStart: citation.videoStart,
    cellAnchor: citation.cellAnchor,
  };
}

function NavigableFileCitationLink({
  citation,
  index,
  onCitationClick,
}: {
  citation: TicketMessageCitation;
  index: number;
  onCitationClick?: (citation: CitationTarget) => void;
}) {
  if (!isFileCitation(citation)) return null;

  const label =
    citation.videoStart != null
      ? formatTimeStamp(citation.videoStart)
      : citation.cellAnchor != null
        ? citationLabel(citation)
        : "Source";
  const ariaLabel =
    citation.videoStart != null
      ? `Navigate to source at ${label}`
      : citation.cellAnchor != null
        ? `Navigate to source in ${label}`
        : "Navigate to source";

  return (
    <button
      key={`file-${index}`}
      type="button"
      data-testid="comment-panel-citation"
      onClick={() => onCitationClick?.(fileCitationToTarget(citation))}
      className="inline-flex cursor-pointer items-center gap-1 border-0 bg-transparent p-0 text-puntt-accent-11 underline underline-offset-2 hover:bg-transparent hover:text-puntt-accent-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-puntt-accent-9"
      aria-label={ariaLabel}
    >
      <Text as="span" size="1" weight="medium">
        {label}
      </Text>
      <ArrowSquareOut size={12} aria-hidden="true" />
    </button>
  );
}

function TextCitationLink({ citation }: { citation: TicketMessageCitation }) {
  if (!isTextCitation(citation)) return null;
  const label = citationLabel(citation);

  return (
    <Dialog.Root>
      <Dialog.Trigger>
        <button
          type="button"
          data-testid="comment-panel-citation-text"
          className="inline-flex cursor-pointer items-center gap-1 border-0 bg-transparent p-0 text-puntt-accent-11 underline underline-offset-2 hover:bg-transparent hover:text-puntt-accent-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-puntt-accent-9"
        >
          <Text as="span" size="1" weight="medium">
            {label}
          </Text>
          <ArrowSquareOut size={12} aria-hidden="true" />
        </button>
      </Dialog.Trigger>
      <Dialog.Content className="flex max-h-[min(70vh,420px)] flex-col gap-4 overflow-y-auto">
        <Dialog.Title>Guideline source</Dialog.Title>
        <Dialog.Description className="hidden">
          Guideline excerpt
        </Dialog.Description>
        <Text size="2" className="text-puntt-neutral-gray-11">
          <em>From custom guidelines:</em>
        </Text>
        <Preview text={citation.text} />
        <Dialog.Close>
          <Button type="button" variant="soft" color="gray">
            Close
          </Button>
        </Dialog.Close>
      </Dialog.Content>
    </Dialog.Root>
  );
}

function KnowledgeDbCitationLinkInner({
  citation,
}: {
  citation: TicketMessageCitation & { knowledgeDataRecordId: string };
}) {
  const recordId = citation.knowledgeDataRecordId;
  const getKnowledgeDataRecord = useGetKnowledgeDataRecord(recordId);
  const [recordPreview, setRecordPreview] = useState<
    KnowledgeDataRecordProperty[] | undefined
  >();

  useEffect(() => {
    if (!getKnowledgeDataRecord.data) return;
    setRecordPreview(getKnowledgeDataRecord.data.data);
  }, [getKnowledgeDataRecord.data]);

  const trigger = (
    <button
      type="button"
      data-testid="comment-panel-citation-kb"
      className="inline-flex cursor-pointer items-center gap-1 border-0 bg-transparent p-0 text-puntt-accent-11 underline underline-offset-2 hover:bg-transparent hover:text-puntt-accent-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-puntt-accent-9"
    >
      <Text as="span" size="1" weight="medium">
        Knowledge DB
      </Text>
      <ArrowSquareOut size={12} aria-hidden="true" />
    </button>
  );

  if (getKnowledgeDataRecord.isError) {
    return (
      <Tooltip content="Source was deleted from the Knowledge Database">
        <span className="inline-flex items-center gap-1 opacity-50">
          {trigger}
        </span>
      </Tooltip>
    );
  }

  if (getKnowledgeDataRecord.isLoading) {
    return <span className="inline-flex opacity-50">{trigger}</span>;
  }

  return (
    <Dialog.Root>
      <Dialog.Trigger>{trigger}</Dialog.Trigger>
      <Dialog.Content className="flex max-h-[min(70vh,420px)] flex-col gap-4 overflow-y-auto">
        <Dialog.Title>Knowledge Database source</Dialog.Title>
        <Dialog.Description className="hidden">
          Knowledge database record
        </Dialog.Description>
        <Flex justify="end">
          <Button variant="outline" size="2" asChild>
            <Link
              to={knowledgeDatabaseRoute.to}
              search={(): any => {
                const modelId = getKnowledgeDataRecord.data?.modelId;
                return modelId ? { tab: modelId } : {};
              }}
              target="_blank"
              rel="noreferrer"
              className="puntt-link flex gap-1"
            >
              <Text>View Knowledge Database</Text>
              <ArrowSquareOut width={16} height={16} />
            </Link>
          </Button>
        </Flex>
        <Preview knowledgeDataRecord={recordPreview} />
        <Dialog.Close>
          <Button type="button" variant="soft" color="gray">
            Close
          </Button>
        </Dialog.Close>
      </Dialog.Content>
    </Dialog.Root>
  );
}

function KnowledgeDbCitationLink({
  citation,
}: {
  citation: TicketMessageCitation;
}) {
  if (!isKnowledgeDbCitation(citation)) return null;
  return <KnowledgeDbCitationLinkInner citation={citation} />;
}

export function CommentCitationLinks({
  citations,
  onCitationClick,
}: {
  citations: TicketMessageCitation[];
  onCitationClick?: (citation: CitationTarget) => void;
}) {
  if (citations.length === 0) return null;

  return (
    <Flex gap="2" wrap="wrap" className="mt-2 shrink-0">
      {citations.map((citation, idx) => {
        if (isNavigableFileCitation(citation)) {
          return (
            <NavigableFileCitationLink
              key={`citation-${idx}`}
              citation={citation}
              index={idx}
              onCitationClick={onCitationClick}
            />
          );
        }
        if (isTextCitation(citation)) {
          return (
            <TextCitationLink key={`citation-${idx}`} citation={citation} />
          );
        }
        if (isKnowledgeDbCitation(citation)) {
          return (
            <KnowledgeDbCitationLink
              key={`citation-${idx}`}
              citation={citation}
            />
          );
        }
        return (
          <Flex
            key={`citation-${idx}`}
            gap="1"
            align="center"
            className="text-puntt-neutral-gray-9"
          >
            <Text size="1">Source</Text>
            <Warning size={12} aria-hidden="true" />
          </Flex>
        );
      })}
    </Flex>
  );
}
